import React from 'react';
import { flag } from '../../config';
import { Link } from 'react-router-dom';
import {
  StarIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  TagIcon,
  ChartBarIcon,
  CreditCardIcon,
  UsersIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { ROLES } from './utils/roleUtils';
import { DEFAULT_AUDIENCE_ITEMS } from '../public/AudienceNav';

// Under SLOT_MODEL the drawer shows these as its audience links (MobileNavAudienceLinks),
// so the role lists leave them out rather than link to the same page twice.
const AUDIENCE_PATHS = new Set(DEFAULT_AUDIENCE_ITEMS.map((item) => item.to));

/**
 * Secondary Navigation Links
 * 
 * Role-specific links that appear below the primary action.
 */
export default function MobileNavLinks({ role, user, onClose }) {
  const roleLinks = getLinksForRole(role, user);
  const links = flag('SLOT_MODEL')
    ? roleLinks.filter((link) => link.divider || !AUDIENCE_PATHS.has(link.to))
    : roleLinks;
  
  return (
    <div className="space-y-1 mb-6">
      {links.map((link, index) => {
        // Handle divider items
        if (link.divider) {
          return (
            <div key={index} className="flex items-center gap-3 pt-4 pb-2">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/10" />
              <span className="text-xs text-white/30 uppercase tracking-wider font-medium">
                {link.label}
              </span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/10" />
            </div>
          );
        }
        
        // `href` items (mailto:, external) render as a plain anchor.
        const Wrapper = link.href ? 'a' : Link;
        const wrapperProps = link.href ? { href: link.href } : { to: link.to };

        return (
          <Wrapper
            key={index}
            {...wrapperProps}
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg hover:bg-white/5 transition-colors"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${link.iconBg || 'bg-white/10'}`}>
              {link.emoji ? (
                <span className="text-sm">{link.emoji}</span>
              ) : (
                <link.icon className={`w-4 h-4 ${link.iconColor || 'text-white/50'}`} />
              )}
            </div>
            <span className="text-white/70 text-sm">{link.label}</span>
            {link.badge && (
              <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${link.badgeColor || 'bg-white/10 text-white/50'}`}>
                {link.badge}
              </span>
            )}
            {link.value && (
              <span className={`ml-auto text-xs font-medium ${link.valueColor || 'text-white/40'}`}>
                {link.value}
              </span>
            )}
          </Wrapper>
        );
      })}
    </div>
  );
}

// Customer-facing links available to all logged-in users
const customerFacingLinks = [
  {
    to: '/local-deals-near-me',
    label: 'Find Deals',
    emoji: '🎁',
    iconBg: 'bg-orange-500/10',
  },
  {
    to: '/how-it-works',
    label: 'How It Works',
    icon: InformationCircleIcon,
    iconBg: 'bg-white/10',
  },
  {
    to: '/our-story',
    label: "Founder's Story",
    emoji: '📖',
    iconBg: 'bg-amber-500/10',
  },
  {
    to: '/pricing',
    label: 'Pricing',
    emoji: '💰',
    iconBg: 'bg-green-500/10',
  },
];

function getLinksForRole(role, user) {
  const commonLinks = [
    {
      to: '/profile',
      label: 'Settings',
      icon: Cog6ToothIcon,
    },
  ];
  
  switch (role) {
    case ROLES.CONSUMER:
      return [
        ...(flag('REWARDS') ? [{
          to: '/rewards',
          label: 'My Taps & Rewards',
          emoji: '⭐',
          iconBg: 'bg-orange-500/20',
          value: `${user?.taps_balance || 0} Taps`,
          valueColor: 'text-orange-400',
        }] : []),
        {
          to: '/how-it-works',
          label: 'How It Works',
          icon: InformationCircleIcon,
        },
        {
          to: '/our-story',
          label: "Founder's Story",
          emoji: '📖',
          iconBg: 'bg-amber-500/10',
        },
        ...commonLinks,
      ];
    
    case ROLES.BUSINESS:
      return [
        {
          to: '/business/deals',
          label: 'My Deals',
          icon: TagIcon,
          iconBg: 'bg-purple-500/20',
          iconColor: 'text-purple-400',
          badge: user?.active_deals_count ? `${user.active_deals_count} Active` : null,
          badgeColor: 'bg-green-500/20 text-green-400',
        },
        {
          to: '/business/dashboard',
          label: 'Dashboard',
          icon: ChartBarIcon,
        },
        // Phase 12: with SLOT_MODEL on the legacy plan pages redirect, so the entry becomes
        // "Slots & billing" (SLOT_BILLING on too) or goes away (SLOT_MODEL alone), as in the
        // portal nav. The session user carries no subscription details, so a business still
        // paying for a legacy plan reaches /business/subscription-manage (kept) from "Manage
        // billing" in Business details (SlotSettings) instead.
        ...(!flag('SLOT_MODEL') ? [{
          to: '/business/subscription-manage',
          label: 'Subscription',
          icon: CreditCardIcon,
        }] : flag('SLOT_BILLING') ? [{
          to: '/business/billing',
          label: 'Slots & billing',
          icon: CreditCardIcon,
        }] : []),
        // Divider marker for customer-facing section
        { divider: true, label: 'Explore' },
        ...customerFacingLinks,
        ...commonLinks,
      ];
    
    case ROLES.VENUE:
      return [
        // Phase 8B: with SLOT_MODEL on, coasters arrive with each quarterly print cycle, so the
        // venue sees its deliveries; the reorder email and Scan Analytics (whose route redirects
        // to the dashboard, routes/venue.jsx) go, as in the portal nav (DashboardNavLinks).
        ...(flag('SLOT_MODEL') ? [{
          to: '/venue/deliveries',
          label: 'Deliveries',
          icon: TruckIcon,
          iconBg: 'bg-cyan-500/20',
        }] : [
          // PD-14: reordering is an email request, not an inventory screen.
          {
            href: 'mailto:hello@tappd.app?subject=Coaster%20reorder%20request',
            label: 'Request More Coasters',
            emoji: '🍺',
            iconBg: 'bg-cyan-500/20',
          },
          ...(flag('VENUE_ANALYTICS') ? [{
            to: '/venue/analytics',
            label: 'Scan Analytics',
            icon: ChartBarIcon,
          }] : []),
        ]),
        // Divider marker for customer-facing section
        { divider: true, label: 'Explore' },
        ...customerFacingLinks,
        ...commonLinks,
      ];
    
    case ROLES.ADMIN:
      return [
        {
          to: '/admin/customers',
          label: 'Manage Users',
          icon: UsersIcon,
          iconBg: 'bg-blue-500/20',
          iconColor: 'text-blue-400',
        },
        {
          to: '/admin/businesses',
          label: 'Manage Businesses',
          emoji: '📈',
          iconBg: 'bg-purple-500/20',
        },
        {
          to: '/admin/locations',
          label: 'Manage Hubs',
          emoji: '📍',
          iconBg: 'bg-orange-500/20',
        },
        {
          to: '/admin/venue-applications',
          label: 'Venue Applications',
          emoji: '🍺',
          iconBg: 'bg-cyan-500/20',
        },
        {
          to: '/admin/subscriptions',
          label: 'Subscriptions',
          icon: CreditCardIcon,
          iconBg: 'bg-green-500/20',
          iconColor: 'text-green-400',
        },
        // Divider marker for customer-facing section
        { divider: true, label: 'Customer View' },
        {
          to: '/',
          label: 'Homepage',
          emoji: '🏠',
          iconBg: 'bg-white/10',
        },
        ...customerFacingLinks,
        {
          to: '/for-businesses',
          label: 'For Businesses',
          emoji: '📈',
          iconBg: 'bg-purple-500/10',
        },
        {
          to: '/for-venues',
          label: 'For Venues',
          emoji: '🍺',
          iconBg: 'bg-cyan-500/10',
        },
        ...commonLinks,
      ];
    
    default:
      return commonLinks;
  }
}
